
import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { PATH_DASHBOARD } from '../routes/paths';
import { REST_API } from '../constants/DefaultValues';
import { isValidToken, setSession } from '../utils/jwt';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload

    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload

    return {
      ...state,
      isAuthenticated: true,
      user,
    }
  },
}

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  verifyLoginOtp: () => Promise.resolve(),
  resendLoginOtp: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
})

AuthProvider.propTypes = {
  children: PropTypes.node,
}

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const navigate = useNavigate()
  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken')

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken)
          const user = JSON.parse(localStorage.getItem('user'))
          if (user.user_type !== 1 && user.user_type !== 3) {
            const response = await axios.post(
              `${REST_API}user/check-active-user`,
              { userId: user.user_id },
            )
            if (response.data.status === 1) {
              dispatch({
                type: 'INITIALIZE',
                payload: {
                  isAuthenticated: true,
                  user: null,
                },
              })
            } else {
              dispatch({
                type: 'INITIALIZE',
                payload: {
                  isAuthenticated: false,
                  user,
                },
              })
            }
          } else {
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: true,
                user: null,
              },
            })
          }
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          })
        }
      } catch (err) {
        console.error(err)
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        })
      }
    }

    initialize()
  }, [])

  useEffect(() => {
    const handlePostMessage = async (event) => {
      // const allowedOrigin = `http://localhost:3000`; // Master domain
      const allowedOrigin = `https://master.signwisesolutions.com` // Master domain
      const childOrigin = window.location.origin
      // Ignore messages originating from the child itself
      if (event.origin === childOrigin) {
        console.warn('Ignored message from child origin:', event.origin)
        return
      }

      // Validate the origin of the parent
      if (event.origin !== allowedOrigin) {
        console.error('Blocked message from untrusted origin:', event.origin)
        return
      }

      const { username, password } = event.data

      if (!username || !password) {
        console.error('Missing credentials in the message:', event.data)
        return
      }

      try {
        await login(username, password)

        navigate(PATH_DASHBOARD.root)
      } catch (error) {
        console.error('Error during login via postMessage:', error)
      }
    }
    // window?.opener?.postMessage('child_ready', `http://localhost:3000`);
    window?.opener?.postMessage(
      'child_ready',
      `https://master.signwisesolutions.com`,
    )
    window?.addEventListener('message', handlePostMessage)

    return () => {
      window.removeEventListener('message', handlePostMessage)
    }
  }, [])

  useEffect(() => {
    console.log('Setting up postMessage listener for child window.')

    // Notify parent that the child window is ready
    if (window.opener) {
      window.opener.postMessage('child_ready', window.location.origin)
      // window.opener.postMessage('child_ready', 'http://localhost:3001');
    }

    // Simulate fetching data
    const fetchData = async () => {
      console.log('Fetching data in child window...')
      // Simulate data fetching delay
      await new Promise((resolve) => setTimeout(resolve, 3000)) // Replace with actual data fetching logic

      console.log('Data fetched successfully. Notifying parent...')
      if (window.opener) {
        window.opener.postMessage('data_fetched', window.location.origin)
      }
    }

    fetchData()

    return () => {
      console.log('Cleanup child window listeners.')
    }
  }, [])

  const login = async (usename, password) => {
    const data = {}
    data.username = usename
    data.password = password

    const response = await axios.post(`${REST_API}login`, data)

    if (response.data?.requires2FA || response.data?.status === 2) {
      return response.data
    }

    const { accessToken, user } = response.data
    if (accessToken) {
      localStorage.setItem('user_id', user.user_id)
      localStorage.setItem('user_type', user.user_type)
      localStorage.setItem('user', JSON.stringify(user))
    }
    setSession(accessToken)
    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    })
    return response.data
  }

  const verifyLoginOtp = async (userId, otp) => {
    const response = await axios.post(`${REST_API}verify-login-otp`, {
      userId,
      otp,
    })
    const { accessToken, user } = response.data
    if (accessToken) {
      localStorage.setItem('user_id', user.user_id)
      localStorage.setItem('user_type', user.user_type)
      localStorage.setItem('user', JSON.stringify(user))
    }
    setSession(accessToken)
    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    })
    return response.data
  }

  const resendLoginOtp = async (userId) => {
    const response = await axios.post(`${REST_API}resend-login-otp`, { userId })
    return response.data
  }

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    })
    const { accessToken, user } = response.data

    window.localStorage.setItem('accessToken', accessToken)
    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    })
  }

  const logout = async () => {
    try {
      // Clear session first
      setSession(null)
      
      // Try to logout from server if user data exists
      const userDataStr = window.localStorage.getItem('user')
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr)
          if (userData && userData.user_id) {
            await axios.post(`${REST_API}logout`, {
              userId: userData.user_id,
            })
          }
        } catch (error) {
          console.error('Error during server logout:', error)
          // Continue with local logout even if server logout fails
        }
      }
      
      // Clear all local storage
      localStorage.removeItem('user_id')
      localStorage.removeItem('user_type')
      localStorage.removeItem('user')
      localStorage.removeItem('accessToken')
      
      // Update state
      dispatch({ type: 'LOGOUT' })
    } catch (error) {
      console.error('Logout error:', error)
      // Even if there's an error, clear local data
      setSession(null)
      localStorage.removeItem('user_id')
      localStorage.removeItem('user_type')
      localStorage.removeItem('user')
      localStorage.removeItem('accessToken')
      dispatch({ type: 'LOGOUT' })
    }
  }

  const resetPassword = async (email) => {
    const result = await axios.post(`${REST_API}forgot-password`, {
      email,
    })
    return result
  }
  const loginWithUserId = async (userType, id, isFromClient, isFromAdmin) => {
    const data = {
      id,
      userType,
      isFromClient,
      isFromAdmin,
    }
    const response = await axios.post(`${REST_API}login-using-id`, data)

    const { accessToken, user } = response.data
    if (accessToken) {
      localStorage.setItem('user', JSON.stringify(user))
    }
    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    })
  }
  const updateProfile = () => {}

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        verifyLoginOtp,
        resendLoginOtp,
        logout,
        register,
        resetPassword,
        updateProfile,
        loginWithUserId,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
